import { useMemo } from 'react'
import { GetFormat, postProvider, PostFormat, useGet } from 'sharedKernel/api'
import type {
  IOpportunityListResponse,
  IOpportunitySupportFile,
  IResolverOpportunity,
  IStartupOpportunityDetail,
} from './contracts'

interface StartupOpportunityApiRow {
  id: string
  title?: string
  description?: string
  requesterName?: string
  institutionName?: string
  segment?: string
  operationArea?: string
  verticals?: string[]
  otherVertical?: string | null
  businessModel?: string
  category?: string
  companySize?: string
  interestDeadline?: string
  maxValue?: string
  maximumOpportunityValue?: number | string
  impactedBy?: string
  impactedAudience?: string
  references?: string
  supportLink?: string
  coverImagePath?: string
  style?: {
    coverImagePath?: string
  }
  imagePath?: string
  image?: string
  files?: IOpportunitySupportFile[]
  interestedStartups?: Array<{ name?: string; logo?: string }>
  hasDemonstratedInterest?: boolean
  hasInterest?: boolean
  scouting?: IResolverOpportunity['scouting']
}

interface StartupOpportunityApiList {
  rows?: StartupOpportunityApiRow[]
  totalRows?: number
  totalPages?: number
  offset?: number
  limit?: number
}

interface StartupOpportunityDetailApiResponse {
  id?: string
  data?: StartupOpportunityApiRow
}

interface StartupOpportunityFilesApiResponse {
  rows?: IOpportunitySupportFile[]
}

const formatMaximumOpportunityValue = (
  maximumOpportunityValue?: number | string,
  maxValue?: string
): string | undefined => {
  if (maxValue?.trim()) return maxValue.trim()

  if (maximumOpportunityValue == null || maximumOpportunityValue === '') {
    return undefined
  }

  const numericValue =
    typeof maximumOpportunityValue === 'number'
      ? maximumOpportunityValue
      : parseFloat(
          String(maximumOpportunityValue)
            .replace(/[R$\s]/g, '')
            .replace(/\./g, '')
            .replace(',', '.')
        )

  if (Number.isNaN(numericValue)) {
    return String(maximumOpportunityValue)
  }

  return numericValue.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const mapListRow = (row: StartupOpportunityApiRow): IResolverOpportunity => ({
  id: String(row.id),
  title: row.title ?? '',
  description: row.description ?? '',
  requesterName: row.requesterName ?? row.institutionName,
  institutionName: row.institutionName,
  operationArea: row.operationArea,
  verticals: row.verticals,
  otherVertical: row.otherVertical ?? undefined,
  segment: row.segment,
  businessModel: row.businessModel,
  category: row.category,
  companySize: row.companySize,
  interestDeadline: row.interestDeadline,
  maxValue: formatMaximumOpportunityValue(row.maximumOpportunityValue, row.maxValue),
  maximumOpportunityValue: row.maximumOpportunityValue,
  coverImagePath: row.coverImagePath,
  style: row.style,
  imagePath: row.imagePath,
  image: row.image,
  interestedStartups: row.interestedStartups,
  hasInterest: row.hasInterest === true || row.hasDemonstratedInterest === true,
  scouting: row.scouting,
  files: row.files,
})

const mapDetailRow = (row: StartupOpportunityApiRow): IStartupOpportunityDetail => ({
  ...mapListRow(row),
  impactedBy: row.impactedBy ?? row.impactedAudience,
  impactedAudience: row.impactedAudience,
  references: row.references,
  supportLink: row.supportLink,
  files: row.files,
})

const mapDetailResponse = (
  raw: StartupOpportunityDetailApiResponse | StartupOpportunityApiRow | undefined
): IStartupOpportunityDetail | undefined => {
  if (raw == null) return undefined

  const row = 'data' in raw && raw.data ? raw.data : (raw as StartupOpportunityApiRow)
  if (!row?.id) return undefined

  return mapDetailRow(row)
}

const mapListResponse = (raw: StartupOpportunityApiList | undefined): IOpportunityListResponse | undefined => {
  if (raw == null) return undefined
  const rows = raw.rows ?? []
  return {
    rows: rows.map(mapListRow),
    totalRows: raw.totalRows ?? rows.length,
    page: raw.offset ?? 1,
    limit: raw.limit ?? rows.length,
    totalPages: raw.totalPages ?? 1,
  }
}

export const useGetResolverOpportunities = () => {
  const request: GetFormat = { url: '/v1/startup/opportunity' }
  const { data, isFetching, refetch } = useGet<StartupOpportunityApiList>(request, 'hub-startup-opportunity-list')
  const mapped = useMemo(() => mapListResponse(data), [data])

  return {
    data: mapped,
    isFetching,
    refetch,
  }
}

export const useGetStartupOpportunityById = (opportunityId: string) => {
  const request: GetFormat = { url: `/v1/startup/opportunity/${opportunityId}` }
  const { data, isFetching, refetch } = useGet<
    StartupOpportunityDetailApiResponse | StartupOpportunityApiRow
  >(request, `hub-startup-opportunity-detail-${opportunityId}`, { enabled: !!opportunityId })

  const mapped = useMemo(() => mapDetailResponse(data), [data])

  return {
    data: mapped,
    isFetching,
    refetch,
  }
}

export const useGetStartupOpportunityFiles = (opportunityId: string) => {
  const request: GetFormat = { url: `/v1/startup/opportunity/${opportunityId}/files` }
  const { data, isFetching, refetch } = useGet<StartupOpportunityFilesApiResponse>(
    request,
    `hub-startup-opportunity-files-${opportunityId}`,
    { enabled: !!opportunityId }
  )

  return {
    data: data?.rows ?? [],
    isFetching,
    refetch,
  }
}

export const useRegisterInterest = (
  onSuccess?: (success: unknown) => Promise<void>,
  onError?: (error: unknown) => Promise<void>
) => postProvider(onSuccess, onError)

export const buildRegisterInterestRequest = (opportunityId: string): PostFormat => ({
  url: `/v1/startup/opportunity/${opportunityId}/interest`,
  data: {},
})
